import { useMemo } from "react";
import { useClientFeature } from "@/hooks/useClientFeature";
import { api } from "@/lib/api-client";
import {
  toLocationFilterOptions,
  toResourceFilterOptions,
  toServiceFilterOptions,
  toUserFilterOptions,
} from "./calendarFilterOptions";
import type { CalendarFilterOption } from "./types";

export type CalendarFilterOptions = {
  locations: CalendarFilterOption[];
  users: CalendarFilterOption[];
  services: CalendarFilterOption[];
  resources: CalendarFilterOption[];
  isLocationsLoading: boolean;
  areOptionsReady: boolean;
};

/**
 * Options for a `CalendarFilter` drawer, scoped to match what the calendar grid
 * draws: `/user` with `respectCalendarPermissions` and `/resource` apply the
 * same `calendar_users` / `calendar_resources` gates as `/calendar/2`, and
 * disabled resources are dropped here because the grid's query excludes them.
 *
 * Only an explicit `permission: false` hides an option — the flag is a display
 * hint (the appointment endpoints reject unpermitted ids anyway), so a missing
 * one must never empty a picker.
 */
export function useCalendarFilterOptions(
  locationId: number | null,
): CalendarFilterOptions {
  const { isFeatureEnabled } = useClientFeature();
  const areResourcesEnabled = isFeatureEnabled("resources");

  const { data: locationsData, isLoading: isLocationsLoading } =
    api.location.useGetLocations({ sortBy: "labelAsc" });

  const {
    data: usersData,
    isLoading: isUsersLoading,
    isPlaceholderData: areUsersStale,
  } = api.user.useUserList(
    {
      locationIds: locationId == null ? [] : [locationId],
      respectCalendarPermissions: true,
    },
    locationId == null,
  );

  const { data: servicesData, isLoading: isServicesLoading } =
    api.service.useGetServices({});

  const {
    data: resourcesData,
    isLoading: isResourcesLoading,
    isPlaceholderData: areResourcesStale,
  } = api.resources.useGetResources({
    // -1 is `useGetResources`'s own "skip this query" sentinel.
    locationId: areResourcesEnabled ? (locationId ?? -1) : -1,
  });

  const locations = useMemo<CalendarFilterOption[]>(
    () => toLocationFilterOptions(locationsData),
    [locationsData],
  );

  const users = useMemo<CalendarFilterOption[]>(
    () => toUserFilterOptions(usersData),
    [usersData],
  );

  const services = useMemo<CalendarFilterOption[]>(
    () => toServiceFilterOptions(servicesData),
    [servicesData],
  );

  const resources = useMemo<CalendarFilterOption[]>(
    () => (areResourcesEnabled ? toResourceFilterOptions(resourcesData) : []),
    [areResourcesEnabled, resourcesData],
  );

  // The staff and resource queries keep the previous location's data on screen
  // while the new one loads, so `isLoading` alone cannot tell the two apart.
  const areOptionsStale =
    areUsersStale || (areResourcesEnabled && areResourcesStale);

  // A disabled query reports `isLoading: false`, so the location has to be
  // checked explicitly — otherwise "ready" is true while there is no location
  // to have fetched staff for.
  const areOptionsReady =
    locationId != null &&
    !isLocationsLoading &&
    !isUsersLoading &&
    !isServicesLoading &&
    (!areResourcesEnabled || !isResourcesLoading) &&
    !areOptionsStale;

  return {
    locations,
    users,
    services,
    resources,
    isLocationsLoading,
    areOptionsReady,
  };
}
